import { mapGetters } from "vuex";
import Stomp from "webstomp-client";
import { base64Icons } from "./base64Icons";
import { pickDirectionalIcon, preloadCustomIcons } from "@/maps/customIcons";
import { getBrandMarkerIcon } from "@/maps/brandMarker";
import {
  animateMarker as animatePosition,
  cancelMarkerAnimation,
  createMapClientForPoints,
} from "@/maps";

export default {
  name: "MapTracking",

  props: {
    id: {
      type: String,
      default: "map",
    },
    centerPosition: {
      type: Object,
      default: () => ({ lat: 24.7253981, lng: 46.3398977 }),
    },
    zoom: {
      type: Number,
      default: 8,
    },
    midMile: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      map: null,
      points: [],
      captainMarkers: [],
      pollingIntervalId: null,
      setCenterOnce: true,
      stompOptions: {
        debug: false,
        heartbeat: { outgoing: 0, incoming: 20000 },
      },
    };
  },

  mounted() {
    this.initMap();
    this.startPolling();
  },

  computed: {
    ...mapGetters([
      "getStompConnectionUrl",
      "getStompConnectionsTopics",
      "getStompConnectionToken",
      "getMidMile",
      "getMapPoints",
      "getCustomIcons",
    ]),
  },

  beforeDestroy() {
    this.stopPolling();
    if (this.stompClient) {
      this.stompClient.disconnect();
      this.stompClient = null;
    }
    this.captainMarkers.forEach((m) => cancelMarkerAnimation(m));
    if (this.map && typeof this.map.destroy === "function") {
      this.map.destroy();
    }
    this.map = null;
  },

  methods: {
    initMap() {
      // Choose the map provider based on where the order's pickup and delivery points are.
      const COVERAGE_TYPES = ["PICK_UP", "ON_DEMAND_PICK_UP", "DELIVERY"];
      const coveragePoints = Object.values(this.getMapPoints || {})
        .filter((p) => COVERAGE_TYPES.includes(p.type))
        .map((p) => ({ lat: p.latitude, lng: p.longitude }))
        .filter((p) => typeof p.lat === "number" && typeof p.lng === "number");

      createMapClientForPoints(coveragePoints)
        .then((client) => {
          this.map = client.createMap(this.id, {
            center: this.getCenterPosition(),
            zoom: this.zoom,
          });

          if (this.getMidMile) {
            this.map.fitBounds([
              { lat: 12.5, lng: 34.5 },
              { lat: 30.5, lng: 56.5 },
            ]);
          }

          preloadCustomIcons(this.getCustomIcons);
          this.prepareItems();
          this.connect();
        })
        .catch((error) => {
          console.error("Map initialization error:", error);
        });
    },

    connect() {
      this.stompClient = Stomp.client(
        this.getStompConnectionUrl,
        this.stompOptions
      );

      this.stompClient.connect(
        { "X-Authorization": `Bearer ${this.getStompConnectionToken}` },
        () => {
          for (const topic of this.getStompConnectionsTopics) {
            this.stompClient.subscribe(topic, (tick) => {
              let responseBody = JSON.parse(tick.body);
              let ourMarker = {
                latitude: responseBody.latitude,
                longitude: responseBody.longitude,
                type: "CAPTAIN",
                id: responseBody.captainId,
              };
              let existingMarker = this.captainMarkers.find(
                (item) => item.id === ourMarker.id
              );

              if (!existingMarker) {
                this.addMarker(ourMarker);
              } else {
                this.animateMarker(existingMarker, 2, ourMarker);
              }
            });
          }
        },
        (error) => {
          console.error("STOMP connection error:", error);
        }
      );
    },

    startPolling() {
      this.pollingIntervalId = setInterval(() => {
        this.pollMapPoints();
      }, 300000);
    },

    stopPolling() {
      if (this.pollingIntervalId) {
        clearInterval(this.pollingIntervalId);
        this.pollingIntervalId = null;
      }
    },

    async pollMapPoints() {
      try {
        await this.$store.dispatch("GET_ORDER_TRACKING_REQUEST");

        const points = this.getMapPoints;

        Object.keys(points).forEach((key) => {
          const point = points[key];
          if (point.type !== "CAPTAIN") {
            const existingMarker = this.captainMarkers.find(
              (m) => m.id === point.id
            );

            if (!existingMarker) {
              this.addMarker(point);
            } else {
              this.animateMarker(existingMarker, 0.5, point);

              if (
                (point.type === "PICK_UP" ||
                  point.type === "ON_DEMAND_PICK_UP") &&
                point.taskCompleted === true
              ) {
                existingMarker.setIcon(this.getIconByType(point));
              }
            }
          }
        });
      } catch (error) {
        console.error("Polling error:", error);
      }
    },

    collectMarkerPositions() {
      const positions = [];
      Object.values(this.getMapPoints || {}).forEach((p) => {
        if (
          p.type !== "CAPTAIN" &&
          typeof p.latitude === "number" &&
          typeof p.longitude === "number"
        ) {
          positions.push({ lat: p.latitude, lng: p.longitude });
        }
      });
      this.captainMarkers.forEach((m) => {
        const pos = m.getPosition && m.getPosition();
        if (pos && typeof pos.lat === "number" && typeof pos.lng === "number") {
          positions.push({ lat: pos.lat, lng: pos.lng });
        }
      });
      return positions;
    },

    fitAllMarkers() {
      if (!this.map) return;
      const positions = this.collectMarkerPositions();
      if (!positions.length) return;
      this.map.fitBounds(positions, { maxZoom: 16, padding: 60 });
    },

    setMapCenter(position) {
      if (this.map) {
        this.map.setCenter(position ? position : this.getCenterPosition());
      }
    },

    setPoints() {
      let points = [];
      Object.keys(this.getMapPoints).forEach((key) => {
        let item = this.getMapPoints[key];
        points.push({
          0: item.longitude,
          1: item.latitude,
        });
      });
      this.points = points;
    },

    addMarkers() {
      Object.keys(this.getMapPoints).forEach((key) => {
        this.addMarker(this.getMapPoints[key]);
      });
    },

    addMarker(options) {
      if (!this.map) return;

      const position = { lat: options.latitude, lng: options.longitude };

      if (options.type === "CAPTAIN") {
        let initialIcon = this.getIconByType(options);
        let initialRotation = null;

        const custom = pickDirectionalIcon(
          this.getCustomCaptainIcons(options.bikeCaptain),
          options.bikeCaptain ? 90 : 0
        );

        if (custom) {
          initialIcon = custom.url;
          initialRotation = custom.rotation;
        } else if (options.bikeCaptain) {
          initialIcon = require("@/assets/theme/icons/task-types/CAPTAIN_BIKE_0_RIGHT.png");
        }

        const marker = this.map.addCaptainMarker({
          position,
          iconUrl: initialIcon,
        });

        marker.id = options.id;
        marker.bikeCaptain = options.bikeCaptain;
        marker.currentIconUrl = initialIcon;

        if (initialRotation !== null && typeof marker.setRotation === "function") {
          marker.setRotation(initialRotation);
        }

        this.captainMarkers.push(marker);

        if (!this.getMidMile && this.setCenterOnce) {
          this.setCenterOnce = false;
          this.fitAllMarkers();
        }
        return;
      }

      let iconUrl;
      let size = 40;
      if (
        this.getMidMile &&
        (options.type === "DELIVERY" || options.type === "PICK_UP") &&
        options.displayNumber !== undefined
      ) {
        iconUrl = this.makeSvgWithNumber(
          base64Icons[options.type],
          options.displayNumber
        );
        size = 60;
      } else {
        iconUrl = this.getIconByType(options);
      }

      const marker = this.map.addIconMarker({
        position,
        iconUrl,
        size,
        zIndex: 1,
      });
      marker.taskCompleted = options.taskCompleted;

      const logoUrl = this.getBrandLogoUrl(options);
      if (logoUrl) {
        getBrandMarkerIcon(logoUrl).then((pinIcon) => {
          if (pinIcon) marker.setIcon(pinIcon);
        });
      }
    },

    getBrandLogoUrl({ type, taskCompleted }) {
      if (this.getMidMile || taskCompleted) return null;
      if (type !== "PICK_UP" && type !== "ON_DEMAND_PICK_UP") return null;
      return (this.getCustomIcons || {}).pickup || null;
    },

    makeSvgWithNumber(base64, number) {
      const svg = `
        <svg width="60" height="60" xmlns="http://www.w3.org/2000/svg">
          <image href="${base64}" x="10" y="10" width="40" height="40" />
          <circle cx="45" cy="15" r="10" fill="#ff4d4f" />
          <text x="45" y="20" font-size="12" font-family="Arial" font-weight="bold" fill="#fff" text-anchor="middle">${number}</text>
        </svg>
      `;
      return "data:image/svg+xml;charset=UTF-8," + encodeURIComponent(svg);
    },

    smoothZoom(zoomVal) {
      var vm = this;
      if (vm.map.getZoom() != zoomVal) {
        setTimeout(function() {
          vm.setMapZoom(zoomVal);
        }, 150);
      }
    },

    getBikeIconByHeading(heading) {
      const degree = (heading + 360) % 360;
      const adjusted = (450 - degree) % 360;
      const snapped = (Math.round(adjusted / 45) * 45) % 360;

      switch (snapped) {
        case 0:
          return require("@/assets/theme/icons/task-types/CAPTAIN_BIKE_0_RIGHT.png");
        case 45:
          return require("@/assets/theme/icons/task-types/CAPTAIN_BIKE_45_UP_RIGHT.png");
        case 90:
          return require("@/assets/theme/icons/task-types/CAPTAIN_BIKE_90_UP.png");
        case 135:
          return require("@/assets/theme/icons/task-types/CAPTAIN_BIKE_135_UP_LEFT.png");
        case 180:
          return require("@/assets/theme/icons/task-types/CAPTAIN_BIKE_180_LEFT.png");
        case 225:
          return require("@/assets/theme/icons/task-types/CAPTAIN_BIKE_225_DOWN_LEFT.png");
        case 270:
          return require("@/assets/theme/icons/task-types/CAPTAIN_BIKE_270_DOWN.png");
        case 315:
          return require("@/assets/theme/icons/task-types/CAPTAIN_BIKE_315_DOWN_RIGHT.png");
        default:
          return require("@/assets/theme/icons/task-types/CAPTAIN_BIKE_0_RIGHT.png");
      }
    },

    animateMarker(marker, duration, moveto) {
      animatePosition(
        marker,
        { lat: moveto.latitude, lng: moveto.longitude },
        duration,
        (heading) => {
          const custom = pickDirectionalIcon(
            this.getCustomCaptainIcons(marker.bikeCaptain),
            heading
          );

          if (custom) {
            if (marker.currentIconUrl !== custom.url) {
              marker.setIcon(custom.url);
              marker.currentIconUrl = custom.url;
            }
            if (typeof marker.setRotation === "function") {
              marker.setRotation(custom.rotation);
            }
          } else if (marker.bikeCaptain) {
            marker.setIcon(this.getBikeIconByHeading(heading));
          } else if (typeof marker.setRotation === "function") {
            marker.setRotation(heading);
          }
        }
      );
    },

    getCustomCaptainIcons(bikeCaptain) {
      if (this.getMidMile) return [];
      const icons = this.getCustomIcons || {};
      return (bikeCaptain ? icons.bike : icons.car) || [];
    },

    getCustomPointIcon(type, taskCompleted) {
      if (this.getMidMile) return null;
      const icons = this.getCustomIcons || {};
      const isPickup = type === "PICK_UP" || type === "ON_DEMAND_PICK_UP";

      if (isPickup && taskCompleted === true) return icons.checkmark || null;
      if (isPickup) return icons.pickup || null;
      // Per SAM-5283 only pickups get a completed state; deliveries keep the customer icon.
      if (type === "DELIVERY") return icons.customer || null;
      return null;
    },

    getIconByType(options) {
      const { type, bikeCaptain, taskCompleted } = options;

      if (type !== "CAPTAIN") {
        const customIcon = this.getCustomPointIcon(type, taskCompleted);
        if (customIcon) return customIcon;
      }

      if (this.getMidMile && type === "CAPTAIN") {
        return require("@/assets/theme/icons/task-types/CAPTAIN.png");
      }

      if (type === "CAPTAIN" && bikeCaptain === true) {
        return require("@/assets/theme/icons/task-types/CAPTAIN_BIKE.svg");
      }

      if (
        (type === "PICK_UP" || type === "ON_DEMAND_PICK_UP") &&
        taskCompleted === true
      ) {
        return require("@/assets/theme/icons/task-types/PICK_UP_COMPLETE.svg");
      }

      const icons = {
        PICK_UP: require("@/assets/theme/icons/task-types/PICK_UP.svg"),
        ON_DEMAND_PICK_UP: require("@/assets/theme/icons/task-types/PICK_UP.svg"),
        DELIVERY: require("@/assets/theme/icons/task-types/DELIVERY.svg"),
        CAPTAIN: require("@/assets/theme/icons/task-types/CAPTAIN_CAR.svg"),
        DEFAULT: require("@/assets/theme/icons/task-types/PICK_UP.svg"),
      };

      const icon = icons[type] ?? icons["DEFAULT"];
      return icon?.default ?? icon;
    },

    setMapZoom(zoom) {
      if (this.map) {
        this.map.setZoom(zoom ? zoom : this.zoom);
      }
    },

    prepareItems() {
      this.setPoints();
      this.addMarkers();
      if (this.getMidMile) {
        this.setMapCenter();
        this.setMapZoom(13);
      } else {
        this.fitAllMarkers();
      }
    },

    getCenterPosition() {
      if (this.points.length) {
        let max_x = Math.max.apply(
          Math,
          this.points.map(function(point) {
            return point[1]; // return latitude
          })
        );
        let min_x = Math.min.apply(
          Math,
          this.points.map(function(point) {
            return point[1];
          })
        );

        let max_y = Math.max.apply(
          Math,
          this.points.map(function(point) {
            return point[0]; // return longitude
          })
        );
        let min_y = Math.min.apply(
          Math,
          this.points.map(function(point) {
            return point[0];
          })
        );

        return {
          lat: (max_x + min_x) / 2,
          lng: (max_y + min_y) / 2,
        };
      }
      return this.centerPosition;
    },
  },
};
